import { CompanyParametersQuery, ContextKeys, convertItemToBaseCurrency, CurrencyCollectionQuery, CurrencyItem, DocumentCalculatorBoPart, DocumentCalculatorModelPart, propertyChangedFilter, VatRateForPurchaseQuery, VatRateForSaleQuery, VatRateItem } from '@skylab-shared/business';
import { BusinessObjectPartContext } from '@skylab-shared/core';
import { CalculationMethod, ExchangeRateModel, InventoryCostCalculationMethod, InventoryDocumentItemModel, InventoryDocumentKind, VatSummary } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding } from '@skylab-shared/utils';
import { CalculatedDocument, DocumentAdjustmentCalculator, DocumentAdjustmentGrossCalculator, DocumentAdjustmentNetCalculator, DocumentCalculator, DocumentGrossCalculator, DocumentNetCalculator } from '@skylab/calculations/document';
import { DocumentItemChangesProperties } from '@skylab/calculations/document-item';

export interface InventoryDocumentCalculatorModelPart extends DocumentCalculatorModelPart<InventoryDocumentItemModel> {
    kind: InventoryDocumentKind;
    costCalculationMethod: InventoryCostCalculationMethod;
    vatSummary?: VatSummary;
}

const InvnetoryCalculatorsMap = {
    [InventoryDocumentKind.GoodsIssue]: {
        [CalculationMethod.Net]: DocumentNetCalculator,
        [CalculationMethod.Gross]: DocumentGrossCalculator
    },
    [InventoryDocumentKind.InternalGoodsIssue]: {
        [CalculationMethod.Net]: DocumentNetCalculator,
        [CalculationMethod.Gross]: DocumentGrossCalculator
    },
    [InventoryDocumentKind.GoodsIssueAdjustment]: {
        [CalculationMethod.Net]: DocumentAdjustmentNetCalculator,
        [CalculationMethod.Gross]: DocumentAdjustmentGrossCalculator
    },
    [InventoryDocumentKind.GoodsReceipt]: {
        [CalculationMethod.Net]: DocumentNetCalculator,
        [CalculationMethod.Gross]: DocumentGrossCalculator
    },
    [InventoryDocumentKind.InternalGoodsReceipt]: {
        [CalculationMethod.Net]: DocumentNetCalculator,
        [CalculationMethod.Gross]: DocumentGrossCalculator
    },
    [InventoryDocumentKind.GoodsReceiptAdjustment]: {
        [CalculationMethod.Net]: DocumentAdjustmentNetCalculator,
        [CalculationMethod.Gross]: DocumentAdjustmentGrossCalculator
    }
};

export class InventoryDocumentCalculatorBoPart extends DocumentCalculatorBoPart<InventoryDocumentCalculatorModelPart, InventoryDocumentItemModel> {
    public override async initializeAsync(context: BusinessObjectPartContext<InventoryDocumentCalculatorModelPart>): Promise<void> {
        await super.initializeAsync(context);
        await this.bo.localContext.preload(VatRateForPurchaseQuery);

        this.registerCostRecalculationAfterExchangeRateChange();
    }

    private registerCostRecalculationAfterExchangeRateChange(): void {
        this.coreFeature.propertyChanged.listen<ExchangeRateModel>(propertyChangedFilter(
            ExchangeRateModel,
            ['rate', 'units'],
            _ => {
                if (this.isGoodsIssueDocument || this.isGoodsIssueAdjustmentDocument) return;

                this.bo.model.items.forEach(elem => {
                    this.recalculateAndRewrite_goodsReceiptInventoryCost(elem, this.bo.model.exchangeRate);
                });
            }
        ));
    }

    protected override recalculateTItemModelAndRewrite(itemModel: InventoryDocumentItemModel, exchangeRate: ExchangeRateModel, updatedProperty?: DocumentItemChangesProperties): void {
        super.recalculateTItemModelAndRewrite(itemModel, exchangeRate, updatedProperty);

        if (this.isGoodsReceiptDocument) this.recalculateAndRewrite_goodsReceiptInventoryCost(itemModel, exchangeRate);
        if (this.isGoodsReceiptAdjustmentDocument && !itemModel.id) this.recalculateAndRewrite_goodsReceiptInventoryCost(itemModel, exchangeRate);
        if ((this.isGoodsIssueDocument || this.isGoodsIssueAdjustmentDocument) && !itemModel.id) this.recalculateAndRewrite_goodsIssueInventoryCost(itemModel);
    }

    private recalculateAndRewrite_goodsReceiptInventoryCost(itemModel: InventoryDocumentItemModel, exchangeRate: ExchangeRateModel) {
        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.bo.model.currencyId });
        const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery);
        const inForeignCurrency = this.bo.model.currencyId !== this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).id;

        const isGross = this.bo.model.costCalculationMethod === InventoryCostCalculationMethod.Gross;
        let cost = isGross ? itemModel.value_grossAfterDiscount : itemModel.value_netAfterDiscount;
        let price = isGross ? itemModel.price_grossAfterDiscount : itemModel.price_netAfterDiscount;

        if (inForeignCurrency && exchangeRate) {
            const rate = (exchangeRate.rate || 1) / (exchangeRate.units || 1);
            cost = Calc.round(cost * rate, precision, MidpointRounding.AwayFromZero);
            price = Calc.round(price * rate, precision, MidpointRounding.AwayFromZero);
        }

        itemModel.inventoryTotalCost = cost;
        itemModel.inventoryUnitCost = price;
    }

    private recalculateAndRewrite_goodsIssueInventoryCost(itemModel: InventoryDocumentItemModel) {
        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.bo.model.currencyId });
        itemModel.inventoryTotalCost = Calc.round(itemModel.inventoryUnitCost! * itemModel.quantity, precision, MidpointRounding.AwayFromZero);
    }

    public override get documentPropertiesThatTriggerRecalculation(): (keyof InventoryDocumentCalculatorModelPart)[] {
        return [
            ...super.documentPropertiesThatTriggerRecalculation,
            'costCalculationMethod'
        ];
    }

    protected recalculateDocumentModel(documentModel: InventoryDocumentCalculatorModelPart): Readonly<{ inDocumentCurrency: CalculatedDocument, inBaseCurrency: CalculatedDocument }> {
        const result = { inDocumentCurrency: null, inBaseCurrency: null };

        const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery);
        const inForeignCurrency = documentModel.currencyId !== this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).id;
        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: documentModel.currencyId });

        const items = documentModel.items
            .map(documentItem => {
                const { rate } = this.getVatRateItem(documentItem.vatRateId);
                return this.convertDocumentItemToItem(documentItem, {
                    rate,
                    exchangeRate: inForeignCurrency && (documentModel.exchangeRate !== null) ? {
                        rate: documentModel.exchangeRate.rate,
                        units: documentModel.exchangeRate.units
                    } : { rate: 1, units: 1 },
                    calculationMethod: documentModel.calculationMethod,
                    precision
                });
            });

        const calculationContext = { precision };
        const calculator = new InvnetoryCalculatorsMap[documentModel.kind as keyof typeof InvnetoryCalculatorsMap][documentModel.calculationMethod as CalculationMethod.Net | CalculationMethod.Gross]();

        if (calculator instanceof DocumentCalculator) {
            result.inDocumentCurrency = calculator.recalculate(items as any, calculationContext) as any;
        } else if (calculator instanceof DocumentAdjustmentCalculator) {
            result.inDocumentCurrency = calculator.recalculate(
                items,
                inForeignCurrency && (documentModel.exchangeRate !== null) ? documentModel.vatSummary!.currencyVatTable!.items : documentModel.vatSummary!.vatTable.items,
                calculationContext
            ) as any;
        }

        if (inForeignCurrency) {
            const { precision: systemCurrencyPrecision } = this.bo.localContext.getPreloaded<CurrencyItem>(ContextKeys.systemCurrency);

            const inBaseCurrencyItems = documentModel.items
                .map(item => convertItemToBaseCurrency(
                    this.convertDocumentItemToItem(item, {
                        rate: this.getVatRateItem(item.vatRateId).rate,
                        exchangeRate: { rate: 1, units: 1 },
                        calculationMethod: documentModel.calculationMethod,
                        precision
                    }),
                    inForeignCurrency && (documentModel.exchangeRate !== null) ? {
                        rate: documentModel.exchangeRate.rate,
                        units: documentModel.exchangeRate.units
                    } : { rate: 1, units: 1 },
                    systemCurrencyPrecision
                ));

            if (calculator instanceof DocumentCalculator) {
                result.inBaseCurrency = calculator.recalculate(inBaseCurrencyItems as any, calculationContext) as any;
            } else if (calculator instanceof DocumentAdjustmentCalculator) {
                result.inBaseCurrency = calculator.recalculate(inBaseCurrencyItems as any, documentModel.vatSummary!.vatTable.items, calculationContext) as any;
            }
        }
        return result as any;
    }

    protected getVatRateItem(id: number): VatRateItem {
        const query = this.isGoodsIssueDocument || this.isGoodsIssueAdjustmentDocument ? VatRateForSaleQuery : VatRateForPurchaseQuery;
        return this.bo.localContext.firstPreloaded(query, { id });
    }

    private get isGoodsIssueDocument(): boolean {
        return [InventoryDocumentKind.GoodsIssue, InventoryDocumentKind.InternalGoodsIssue].includes(this.bo.model.kind);
    }

    private get isGoodsReceiptDocument(): boolean {
        return [InventoryDocumentKind.GoodsReceipt, InventoryDocumentKind.InternalGoodsReceipt].includes(this.bo.model.kind);
    }

    private get isGoodsReceiptAdjustmentDocument(): boolean {
        return this.bo.model.kind === InventoryDocumentKind.GoodsReceiptAdjustment;
    }

    private get isGoodsIssueAdjustmentDocument(): boolean {
        return this.bo.model.kind === InventoryDocumentKind.GoodsIssueAdjustment;
    }
}
